import { AnimatePresence, motion, useReducedMotion, type MotionProps } from "framer-motion";
import type { GenieWelcomeScreenProps } from "@/types/voice/genie-welcome";
import { buildHelpGenieAttributionUrl } from "@/lib/helpGenieAttributionLink";
import { ConnectChooser } from "./ConnectChooser";
import { GenieMist } from "./GenieMist";
import { CustomerMemoryControl } from "./CustomerMemoryControl";

/** First-visit introduction focused on voice and text.
 * Contact and sharing tools stay behind disclosure. */
export const GenieWelcomeScreen = ({
  open,
  brandColor,
  secondaryColor,
  agentName,
  agentDescription,
  logoUrl,
  hideAttribution,
  hasPhone,
  hasEmail,
  phoneNumber,
  emailAddress,
  pageUrl,
  urlName,
  memoryConsent,
  memoryUpdating,
  onMemoryConsentChange,
  onStartVoice,
  onStartText,
}: GenieWelcomeScreenProps) => {
  const end = secondaryColor || "#FF4DB8";
  const reduceMotion = useReducedMotion();
  const description = agentDescription?.trim();
  // Role labels aren't an introduction. Keep an owner's actual descriptive copy.
  const hasDescription = description && !/^(support|customer support|sales|assistant|customer service)[.!]?$/i.test(description);

  const stagger = (i: number): MotionProps => ({
    initial: { opacity: 0, y: reduceMotion ? 0 : 8 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: reduceMotion ? 0 : 0.25, delay: reduceMotion ? 0 : i * 0.05 },
  });

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="genie-welcome"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.25 }}
          className="absolute inset-0 z-[1600] overflow-y-auto bg-genie-background"
          role="region"
          aria-label={`Welcome to ${agentName || "your Genie"}`}
        >
          {!reduceMotion && <GenieMist brandColor={brandColor} secondaryColor={end} />}

          <div className="relative mx-auto flex min-h-full w-full max-w-lg flex-col justify-center px-6 py-8 sm:py-12">
            <motion.div {...stagger(0)} className="flex flex-col items-center text-center">
              {logoUrl && (
                <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-2xl border border-genie-border bg-white p-2">
                  <img src={logoUrl} alt="" className="h-full w-full rounded-xl object-contain" />
                </div>
              )}
              <p className="max-w-full break-words text-sm font-semibold text-gray-700">
                {agentName || "Your Genie"}
              </p>
              <h1 className="mt-5 text-3xl font-bold text-[#222B45]">How can I help?</h1>
              <p className="mt-3 max-w-sm break-words text-sm leading-relaxed text-[#64748B] sm:text-base">
                {hasDescription ? description : "Ask a question, talk something through, or find your next step."}
              </p>
            </motion.div>

            <motion.div {...stagger(1)} className="mt-7">
              <ConnectChooser
                variant="gate"
                brandColor={brandColor}
                secondaryColor={end}
                onStartVoice={onStartVoice}
                onStartChat={onStartText}
                phoneNumber={hasPhone ? phoneNumber : null}
                emailAddress={hasEmail ? emailAddress : null}
                pageUrl={pageUrl || (typeof window !== "undefined" ? window.location.href : "")}
                qrLogoUrl={logoUrl}
                disabled={memoryUpdating}
              />
            </motion.div>

            {memoryConsent && onMemoryConsentChange && (
              <motion.div {...stagger(2)} className="mt-5">
                <CustomerMemoryControl
                  status={memoryConsent}
                  isUpdating={memoryUpdating}
                  onChange={onMemoryConsentChange}
                />
              </motion.div>
            )}

            {!hideAttribution && (
              <motion.div {...stagger(3)} className="pt-6">
                <a
                  href={buildHelpGenieAttributionUrl(urlName)}
                  target="_blank"
                  rel="noopener"
                  className="flex items-center justify-center gap-1.5 text-xs text-gray-500 transition-opacity hover:opacity-70"
                >
                  <img src="/helpgenie_logo.png" alt="" className="h-5 w-5 object-contain" />
                  Powered by Help Genie
                </a>
              </motion.div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GenieWelcomeScreen;
